import React from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDiagramProject,
    faClipboardCheck,
    faBoxOpen,
    faChartLine,
    faUserGear,
    faGears,
    faTruckFast,
    faSquareCheck,
    faUsersViewfinder,
    faCalendarWeek,
    faCalendarDays,
    faGaugeHigh,
    faFileInvoice,
    faFileMedical,
    faIdCard,
    faSliders
} from '@fortawesome/free-solid-svg-icons';
import decklepro_logo from '../images/decklepro.png'; // Update with your logo path
import ClientReviews, { clientReviews } from './ClientReviews';

const pageTitle = "DecklePro | Deckle Matching Software for Kraft Paper Mills";
const pageDescription = "DecklePro helps kraft paper mills reduce trim waste, automate deckle matching, plan knife setups, and manage configurable workflows for production, dispatch, invoices, gate passes, lab reports, and mill documentation.";
const canonicalProductPath = "/deckle-matching-software";
const pageImage = "https://www.anmatics.com/decklepro_banner.png";
const pageKeywords = [
    "deckle matching software",
    "deckle matching",
    "paper mill optimization software",
    "reel slitting software",
    "trim waste reduction software",
    "kraft paper mill software",
    "knife setup planning software",
    "paper roll cutting optimization",
    "paper mill invoice software",
    "gate pass software",
    "paper mill lab report software",
    "paper mill dispatch software",
    "paper mill production schedule software",
    "paper mill configurable workflow software"
];

const productFaqs = [
    {
        question: "How can I reduce trim waste in my paper mill?",
        answer: "DecklePro uses a deckle matching engine to generate optimal knife setups, significantly reducing trim waste and improving raw material yield."
    },
    {
        question: "What is the best software for deckle matching?",
        answer: "DecklePro is purpose-built for kraft paper mills, offering intelligent deckle match suggestions, knife setup planning, and slitting optimization in one platform."
    },
    {
        question: "Can I simulate knife combinations before production?",
        answer: "Yes. DecklePro includes simulation tools that allow planners to preview multiple deckle layouts and knife setups before sending them to the shop floor."
    },
    {
        question: "Does DecklePro support invoices, gate passes, and lab reports?",
        answer: "Yes. DecklePro supports invoice, gate pass, and lab report workflows so paper mills can manage dispatch documentation, quality reporting, and production records in the same system."
    },
    {
        question: "Can DecklePro be configured for different paper mill workflows?",
        answer: "Yes. DecklePro includes configurable workflows for pricing, preferred size units, reel numbering, reel locations, production schedules, dispatch rules, packing slip behavior, rebates, financial year order numbering, reports, and document controls."
    },
    {
        question: "Does DecklePro support paper mills in India and abroad?",
        answer: "Yes. DecklePro is designed for kraft paper mills in India and can scale globally with multi-user access, configurable workflows, and customizable settings."
    }
];

const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DecklePro",
    "alternateName": "Deckle Matching Software",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Paper mill optimization software",
    "operatingSystem": "Web",
    "description": pageDescription,
    "url": `https://www.anmatics.com${canonicalProductPath}`,
    "image": pageImage,
    "keywords": pageKeywords.join(", "),
    "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Kraft paper mills, paper manufacturers, reel slitting teams, production planners"
    },
    "featureList": [
        "Deckle matching engine",
        "Trim waste reduction",
        "Knife setup planning",
        "Reel slitting workflow management",
        "Order and stock planning",
        "Invoice generation",
        "Gate pass entry",
        "Lab report management",
        "Insurance report management",
        "Zone report",
        "Roll-number-based lab report",
        "Packing slip controls",
        "Configurable mill workflows",
        "Production schedule configuration",
        "Reel numbering and location configuration",
        "Dispatch documentation",
        "Production reporting",
        "Kraft paper mill optimization"
    ],
    "publisher": {
        "@type": "Organization",
        "name": "Anmatics Technologies",
        "url": "https://www.anmatics.com",
        "logo": "https://www.anmatics.com/anmatics_logo_square_512.png"
    },
    "review": clientReviews.map((review) => ({
        "@type": "Review",
        "reviewBody": review.quote,
        "author": {
            "@type": "Person",
            "name": review.role,
            "affiliation": {
                "@type": "Organization",
                "name": review.company
            }
        },
        "itemReviewed": {
            "@type": "SoftwareApplication",
            "name": "DecklePro"
        },
        "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
        }
    })),
    "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR",
        "availability": "https://schema.org/PreOrder",
        "priceValidUntil": "2026-12-31",
        "url": `https://www.anmatics.com${canonicalProductPath}`
    }
};

const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": productFaqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
        }
    }))
};

const ProductDetails = ({ canonicalPath = canonicalProductPath }) => {
    const pageUrl = `https://www.anmatics.com${canonicalPath}`;
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.anmatics.com/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Deckle Matching Software",
                "item": `https://www.anmatics.com${canonicalProductPath}`
            }
        ]
    };

    return (
    <main className="bg-light">
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <link rel="canonical" href={pageUrl} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:type" content="product" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:image" content={pageImage} />
            <meta property="og:image:alt" content="DecklePro deckle matching software by Anmatics" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content={pageImage} />
            <meta name="twitter:image:alt" content="DecklePro deckle matching software by Anmatics" />
            <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
        {/* Hero Section */}
        <header
            className="text-white text-center py-3"
            style={{
                background: 'var(--anmatics-dark)',
                boxShadow: '0 18px 45px rgba(11, 27, 52, 0.18)'
            }}
        >
            <div className="container">
                <img
                    src={decklepro_logo}
                    alt="DecklePro deckle matching software logo"
                    width="150"
                    height="80"
                    decoding="async"
                    style={{
                        height: '80px',
                        marginBottom: '20px',
                        background: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 2px 8px rgba(28,101,149,0.08)'
                    }}
                />
                <h1 className="display-6 fw-bold" style={{ letterSpacing: '1.5px' }}>
                    <span style={{ color: '#ffffff' }}>Deckle</span>
                    <span style={{ color: 'var(--anmatics-secondary)' }}>Pro</span>
                    <small style={{ color: '#ffffff', fontSize: '1.5rem', marginLeft: '8px' }}>- Deckle Matching Software</small>
                </h1>
            </div>
        </header>

        <div className="container mt-5 px-md-5">
            <div
                className="rounded-4 shadow-lg p-4 p-md-5 position-relative"
                style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                }}
            >
                <div className="mb-4">
                    <h5 className="fw-bold" style={{ color: 'var(--anmatics-dark)', fontSize: '1.5rem' }}>
                        Built for Kraft Paper Mill Planning
                    </h5>
                    <p className="text-muted mb-0">DecklePro brings reel matching, planning, stock, production, dispatch, invoice, gate pass, lab report, and configurable mill workflows into one structured system.</p>
                </div>
                <ul className="list-unstyled row">
                    {[
                        "Purpose-built for kraft paper slitting and deckle optimization",
                        "Automates reel matching and knife setup",
                        "Reduces trim waste and improves raw material yield",
                        "Integrates order and stock tracking into one intelligent dashboard",
                        "Supports invoice, gate pass, and lab report workflows",
                        "Configures pricing, preferred sizes, reel numbering, reel locations, schedules, packing slips, rebates, and reports",
                        "Provides real-time insights with exportable performance reports",
                        "Helps teams plan faster and execute with fewer manual errors"
                    ].map((text, idx) => (
                        <li
                            key={idx}
                            className="col-md-6 d-flex align-items-start mb-3"
                        >
                            <div
                                className="me-3 d-flex justify-content-center align-items-center"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: 'rgba(20, 184, 166, 0.12)',
                                    borderRadius: '50%',
                                    color: 'var(--anmatics-secondary)',
                                    fontSize: '1.1rem'
                                }}
                            >
                                ✓
                            </div>
                            <div style={{ color: 'var(--anmatics-dark)' }}>{text}</div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>

        {/* Features */}
        <section className="py-5">
            <div className="container">
                <h3 className="text-center mb-5 fw-bold contact-title" style={{ color: 'var(--anmatics-primary)' }}>
                    DecklePro Features
                </h3>
                {/*
                  Updated feature cards with rounded icons and dynamic colors
                */}
                <div className="row g-4">
                    {(() => {
                        const iconColors = [
                            { bg: 'rgba(37, 99, 235, 0.10)', color: '#2563EB', icon: faDiagramProject },
                            { bg: 'rgba(20, 184, 166, 0.12)', color: '#14B8A6', icon: faClipboardCheck },
                            { bg: 'rgba(123, 44, 191, 0.10)', color: '#7B2CBF', icon: faBoxOpen },
                            { bg: 'rgba(249, 115, 22, 0.10)', color: '#F97316', icon: faChartLine },
                            { bg: 'rgba(244, 63, 94, 0.10)', color: '#F43F5E', icon: faUserGear },
                            { bg: 'rgba(37, 99, 235, 0.10)', color: '#2563EB', icon: faGears },
                            { bg: 'rgba(20, 184, 166, 0.12)', color: '#14B8A6', icon: faTruckFast },
                            { bg: 'rgba(123, 44, 191, 0.10)', color: '#7B2CBF', icon: faSquareCheck },
                            { bg: 'rgba(249, 115, 22, 0.10)', color: '#F97316', icon: faUsersViewfinder },
                            { bg: 'rgba(244, 63, 94, 0.10)', color: '#F43F5E', icon: faCalendarWeek },
                            { bg: 'rgba(37, 99, 235, 0.10)', color: '#2563EB', icon: faCalendarDays },
                            { bg: 'rgba(20, 184, 166, 0.12)', color: '#14B8A6', icon: faGaugeHigh },
                            { bg: 'rgba(123, 44, 191, 0.10)', color: '#7B2CBF', icon: faFileInvoice },
                            { bg: 'rgba(249, 115, 22, 0.10)', color: '#F97316', icon: faIdCard },
                            { bg: 'rgba(244, 63, 94, 0.10)', color: '#F43F5E', icon: faFileMedical },
                            { bg: 'rgba(37, 99, 235, 0.10)', color: '#2563EB', icon: faSliders },
                            { bg: 'rgba(20, 184, 166, 0.12)', color: '#14B8A6', icon: faClipboardCheck }
                        ];
                        const features = [
                            {
                                title: "Deckle Match Engine",
                                desc: "Generate optimized reel combinations and knife setups to reduce trim loss."
                            },
                            {
                                title: "Order Management",
                                desc: "Track customer orders from entry to dispatch with clear priorities and status."
                            },
                            {
                                title: "Stock Management",
                                desc: "Manage reel stock by size, weight, GSM, and status with better visibility."
                            },
                            {
                                title: "Advanced Reporting",
                                desc: "Review deckle efficiency, order performance, stock levels, and waste analysis."
                            },
                            {
                                title: "User Management",
                                desc: "Use role-based access for admins, planners, managers, and production teams."
                            },
                            {
                                title: "Master Setup",
                                desc: "Configure product types, tolerances, knife rules, and machine specifications."
                            },
                            // New features begin here
                            {
                                title: "Transport Planning",
                                desc: "Plan dispatch logistics with vehicle, route, and schedule information."
                            },
                            {
                                title: "Dispatch Details",
                                desc: "Track dispatch status, vehicle allocation, and delivery timelines."
                            },
                            {
                                title: "Customer Management",
                                desc: "Maintain customer records, order history, preferences, and communication notes."
                            },
                            {
                                title: "Production Plan",
                                desc: "Define production priorities and align them with incoming orders and inventory status."
                            },
                            {
                                title: "Production Schedule",
                                desc: "View machine-wise schedules, shift plans, and execution timelines."
                            },
                            {
                                title: "Dashboard",
                                desc: "Monitor operations, KPIs, alerts, and bottlenecks from one dashboard."
                            },
                            {
                                title: "Invoice Management",
                                desc: "Generate and manage invoice details connected with dispatch and customer records."
                            },
                            {
                                title: "Gate Pass Entry",
                                desc: "Create gate pass records for controlled material movement and dispatch handover."
                            },
                            {
                                title: "Lab Report",
                                desc: "Record and manage lab report details, including roll-based quality checks when required."
                            },
                            {
                                title: "Configurable Mill Workflows",
                                desc: "Configure pricing, preferred size units, reel numbering, reel locations, production schedules, packing slip behavior, rebates, and financial year order numbering."
                            },
                            {
                                title: "Reports & Documents",
                                desc: "Manage invoices, gate passes, lab reports, insurance reports, zone reports, roll-number-based lab reports, and dispatch documentation."
                            }
                        ];
                        return features.map((feature, idx) => {
                            const { bg, color, icon } = iconColors[idx % iconColors.length];
                            return (
                                <div className="col-md-4" key={feature.title}>
                                    <div
                                        className="feature-card bg-white p-4 shadow-sm rounded-3 h-100 animate-fadeIn d-flex flex-column align-items-center text-center"
                                        style={{
                                            borderTop: `4px solid var(--anmatics-primary)`,
                                            transition: 'transform 0.3s, box-shadow 0.3s'
                                        }}
                                    >
                                        <div className="d-flex justify-content-center align-items-center mb-3 rounded-4"
                                            style={{ width: 64, height: 64, backgroundColor: bg }}>
                                            <FontAwesomeIcon icon={icon} style={{ color }} size="lg" />
                                        </div>
                                        <h5 className="fw-semibold" style={{ color: 'var(--anmatics-primary-hover)' }}>{feature.title}</h5>
                                        <p className="mb-0">{feature.desc}</p>
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>
        </section>

        {/* Statistics Section */}
        <section
            className="py-5"
            style={{
                background: 'var(--anmatics-dark)'
            }}
        >
            <div className="container">
                <div className="row text-center text-white">
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="100">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-graph-up text-warning me-2"></i>
                                <span className="counter">90</span>%
                            </div>
                            <h5>Planning Effort</h5>
                            <p className="mb-0 opacity-75">Potential reduction in manual planning work</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="200">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-speedometer2 text-success me-2"></i>
                                <span className="counter">50</span>%
                            </div>
                            <h5>Faster Processing</h5>
                            <p className="mb-0 opacity-75">Quicker order processing time</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="300">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-people text-info me-2"></i>
                                <span className="counter">5</span>+
                            </div>
                            <h5>Active Installations</h5>
                            <p className="mb-0 opacity-75">Paper mill teams using DecklePro</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="400">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-clock text-warning me-2"></i>
                                24<span className="fs-2">/7</span>
                            </div>
                            <h5>Support</h5>
                            <p className="mb-0 opacity-75">Operational support when teams need help</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Process Section */}
        <section className="py-5 process-section">
            <div className="container">
                <div className="text-center mb-5">
                    <h2 className="fw-bold section-title" style={{ color: 'var(--anmatics-primary)' }}>How It Works</h2>
                    <p className="lead text-muted">A straightforward workflow for moving from manual planning to controlled execution.</p>
                </div>
                <div className="row">
                    {[
                        {
                            number: 1,
                            title: "Setup & Configuration",
                            desc: "Configure your system with paper types, machinery specs, and business rules"
                        },
                        {
                            number: 2,
                            title: "Input Orders & Stock",
                            desc: "Enter customer orders and maintain real-time inventory of paper rolls"
                        },
                        {
                            number: 3,
                            title: "Smart Matching",
                            desc: "Let our deckle engine find optimal deckle combinations to minimize waste"
                        },
                        {
                            number: 4,
                            title: "Execute & Report",
                            desc: "Process orders efficiently and generate detailed performance reports"
                        }
                    ].map((step, idx) => (
                        <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay={100 * (idx + 1)} key={step.title}>
                            <div className="bg-white p-4 shadow-sm rounded-3 h-100 text-center animate-fadeIn process-step" style={{ borderTop: `4px solid var(--anmatics-primary)` }}>
                                <div className="process-number fw-bold mb-3" style={{
                                    width: 48,
                                    height: 48,
                                    lineHeight: '48px',
                                    borderRadius: '50%',
                                    background: 'var(--anmatics-primary-hover)',
                                    color: '#fff',
                                    fontSize: 24,
                                    margin: '0 auto 16px auto'
                                }}>{step.number}</div>
                                <h5 className="fw-semibold" style={{ color: 'var(--anmatics-primary-hover)' }}>{step.title}</h5>
                                <p className="mb-0">{step.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>


        {/* Benefits */}
        <section className="bg-white py-5 border-top border-bottom">
            <div className="container text-center">
                <h2 className="fw-bold mb-4 contact-title" style={{ color: 'var(--anmatics-primary)' }}>Why Choose DecklePro?</h2>
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <ul className="list-group list-group-flush text-start fs-6 shadow-sm rounded-3">
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Reduce trim waste and improve yield per parent roll</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Automate reel matching and knife setup calculations</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Track orders and inventory with precision</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Manage invoice, gate pass, and lab report workflows</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Configure pricing, preferred sizes, reel locations, schedules, packing slips, rebates, and financial year order numbers</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Manage insurance reports, zone reports, and roll-number-based lab reports</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Generate real-time reports for smarter planning</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Enable secure, role-based system access for teams</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Highly customizable to suit different paper mills</li>
                            {/* SEO-targeted benefits */}
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Plan and optimize deckle matching using advanced algorithms</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Simulate knife setups before production begins</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Reduce edge trim loss and material scrap</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Increase machine utilization with smarter scheduling</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Designed for kraft paper mills and roll slitting operations</li>
                        </ul>
                        <p className="mt-4 fs-6 text-center">
                            Want to know how it compares to manual planning? <a href="/blogs/manual-vs-software-deckle-matching" style={{ color: 'var(--anmatics-primary-hover)' }}>Read our detailed comparison</a><br />
                            Also check out: <a href="/blogs/mistakes-in-reel-slitting" style={{ color: 'var(--anmatics-primary-hover)' }}>Top 5 Mistakes in Reel Slitting Operations</a>, <a href="/blogs/deckle-matching-optimization" style={{ color: 'var(--anmatics-primary-hover)' }}>Why Deckle Matching Optimization Can Save Lakhs</a>, and <a href="/deckle-matching-software-for-paper-mills" style={{ color: 'var(--anmatics-primary-hover)' }}>DecklePro answer guide</a>.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <ClientReviews />

        {/* FAQ Section */}
        <section className="py-5 bg-light border-top">
            <div className="container">
                <h2 className="fw-bold text-center mb-4" style={{ color: 'var(--anmatics-primary)' }}>Frequently Asked Questions</h2>
                <div className="accordion" id="faqAccordion">
                    {productFaqs.map((faq, idx) => (
                        <div className="accordion-item mb-3" key={idx}>
                            <h2 className="accordion-header" id={`faqHeading${idx}`}>
                                <button
                                    className="accordion-button collapsed"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#faqCollapse${idx}`}
                                    aria-expanded="false"
                                    aria-controls={`faqCollapse${idx}`}
                                    style={{ fontWeight: 600 }}
                                >
                                    {faq.question}
                                </button>
                            </h2>
                            <div
                                id={`faqCollapse${idx}`}
                                className="accordion-collapse collapse"
                                aria-labelledby={`faqHeading${idx}`}
                                data-bs-parent="#faqAccordion"
                            >
                                <div className="accordion-body">
                                    {faq.answer}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Call to Action */}
        <section
            className="py-5 text-white text-center"
            style={{
                background: 'var(--anmatics-dark)'
            }}
        >
            <div className="container">
                <h2 className="fw-bold mb-3 animate-slideDown">Transform Your Paper Mill Planning</h2>
                <p className="mb-4 animate-fadeIn">Start using DecklePro today and streamline your production, reduce waste, and increase profitability.</p>
                <a
                    href="/contact"
                    className="btn btn-light btn-lg px-4"
                    style={{
                        color: 'var(--anmatics-primary)',
                        fontWeight: 600,
                        border: `2px solid var(--anmatics-primary-hover)`,
                        boxShadow: '0 2px 8px rgba(28,101,149,0.08)'
                    }}
                >
                    Request a Demo
                </a>
            </div>
        </section>
    </main>
    );
};

export default ProductDetails;
